/**
 * WorkspaceSwitcher — cambio rápido de empresa desde el header.
 *
 * Dropdown sobre `session.tenants` (la lista que ya viaja en la cookie
 * `zen_session`, compartida entre subdominios). Reemplaza el detour por la
 * página completa `/auth/select-tenant` para el cambio cotidiano. El salto entre
 * subdominios reusa `buildTenantSwitchUrl` y el SSO por cookies (.clobi.io).
 *
 * Solo se renderiza si el usuario tiene 2+ empresas.
 */
import { useState, useMemo, useRef, useEffect } from 'react'
import { useCierreAlClicAfuera } from '@/lib/useCierreAlClicAfuera'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, ChevronDown, Check, Search, BarChart3 } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { buildTenantSwitchUrl } from '@/lib/tenantNav'

export function WorkspaceSwitcher() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { tenants, activeTenant, activeTenantSchema, setActiveTenant } = useAuthStore()
  const [open, setOpen] = useState(false)
  const disparadorRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  useCierreAlClicAfuera(open, [disparadorRef, panelRef], () => setOpen(false))
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const showSearch = tenants.length > 8

  // Vista Consolidada (caso "dueño multi-empresa / holding"): consolida métricas
  // financieras de TUS empresas. Mismo gate que SelectTenantPage: 2+ tenants con
  // permiso de consolidación/reportes. Se ofrece aquí para que no quede escondida
  // ahora que el switcher es el camino diario (antes solo vivía en /auth/select-tenant).
  const canConsolidate = useMemo(() => {
    const withConsolidation = tenants.filter((t) => {
      const perms = t.permissions || {}
      return perms['consolidation']?.includes('view') || perms['reports']?.includes('view')
    })
    return withConsolidation.length >= 2
  }, [tenants])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter(
      (t) => t.name.toLowerCase().includes(q) || t.schema.toLowerCase().includes(q),
    )
  }, [tenants, query])

  useEffect(() => {
    if (open && showSearch) inputRef.current?.focus()
    if (!open) setQuery('')
  }, [open, showSearch])

  // Hook arriba de cualquier return condicional (reglas de hooks).
  if (tenants.length <= 1) return null

  const handleSelect = (schema: string) => {
    setOpen(false)
    if (schema === activeTenantSchema) return
    const target = tenants.find((t) => t.schema === schema)
    if (!target) return
    queryClient.clear()
    const url = buildTenantSwitchUrl(target, tenants)
    if (url) {
      // ⚠️ NO se llama `setActiveTenant` acá: `localStorage` es por ORIGEN, así
      // que escribiría el tenant DESTINO en el subdominio que estamos dejando.
      // Volver después a este host hidrataba con el tenant equivocado — entrar a
      // zenlatam.clobi.io y ver «Marva» en el selector. El destino lo resuelve
      // con el `?t=` que lleva la URL.
      window.location.href = url // cross-subdominio (SSO vía cookies)
    } else {
      setActiveTenant(schema) // mismo host: acá sí hay que setearlo
      navigate('/dashboard', { replace: true })
    }
  }

  return (
    <div className="relative">
      <button ref={disparadorRef}
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-soft transition-colors max-w-[220px]"
        title="Cambiar empresa"
        aria-label="Cambiar empresa"
      >
        <Building2 size={15} className="text-content-subtle shrink-0" />
        <span className="text-sm font-medium text-content truncate hidden sm:block">
          {activeTenant?.name ?? 'Empresa'}
        </span>
        <ChevronDown size={14} className="text-content-subtle shrink-0" />
      </button>

      {open && (
        <>
          <div ref={panelRef}
            className="absolute left-0 top-full mt-1.5 z-50 bg-popover border border-divider rounded-xl shadow-lg w-72 max-h-[70vh] flex flex-col overflow-hidden"
            style={{ backgroundColor: 'rgb(var(--color-popover))' }}
          >
            <div className="px-3 py-2 border-b border-divider shrink-0">
              <p className="text-xs font-semibold text-content-subtle uppercase tracking-wide">
                Cambiar empresa
              </p>
            </div>

            {showSearch && (
              <div className="px-2 py-2 border-b border-divider shrink-0">
                <div className="relative">
                  <Search
                    size={13}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle"
                  />
                  <input aria-label="Buscar empresa"
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar empresa…"
                    className="w-full pl-8 pr-2 py-1.5 text-sm rounded-lg bg-canvas border border-divider text-content placeholder:text-content-subtle focus:outline-none focus:ring-2 focus:ring-focus-ring"
                  />
                </div>
              </div>
            )}

            <ul className="overflow-y-auto scrollbar-slim py-1 min-h-0">
              {filtered.length === 0 && (
                <li className="px-4 py-3 text-sm text-content-subtle text-center">Sin resultados</li>
              )}
              {filtered.map((t) => {
                const isActive = t.schema === activeTenantSchema
                return (
                  <li key={t.schema}>
                    <button
                      onClick={() => handleSelect(t.schema)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-soft transition-colors ${
                        isActive ? 'bg-soft' : ''
                      }`}
                    >
                      {t.logo_url ? (
                        <img
                          src={t.logo_url}
                          alt=""
                          className="w-7 h-7 rounded-lg object-contain bg-canvas shrink-0"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                          <Building2 size={14} className="text-primary" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-content truncate">{t.name}</p>
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs text-content-subtle truncate">{t.schema}</p>
                          {t.country && (
                            <span className="text-[10px] font-semibold text-content-subtle bg-soft px-1 py-0.5 rounded shrink-0">
                              {t.country}
                            </span>
                          )}
                        </div>
                      </div>
                      {isActive && <Check size={15} className="text-primary shrink-0" />}
                    </button>
                  </li>
                )
              })}
            </ul>

            {canConsolidate && (
              <div className="border-t border-divider shrink-0 p-1">
                <button
                  onClick={() => {
                    setOpen(false)
                    navigate('/consolidated')
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-content-muted hover:text-primary hover:bg-soft transition-colors"
                >
                  <BarChart3 size={15} className="shrink-0" />
                  <span className="text-sm font-medium">Vista consolidada</span>
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
